import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SuccessModal from '../../components/ui/SuccessModal';
import Header from '../../components/navigation/Header';
import BottomNav from '../../components/navigation/BottomNav';
import CouponInput from './components/CouponInput';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { createPaymentIntentWithAuthRetry } from '../../utils/createPaymentIntentWithAuthRetry';

const stripeKey = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const StripePaymentForm = ({ clientSecret, paymentIntentId, packageInfo, subtotal, tax, total, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user, refreshCredits } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  const waitForWebhookCompletion = async (intentId, userId, maxAttempts = 8, delayMs = 800) => {
    if (!intentId || !userId) return null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const { data, error } = await supabase
        ?.from('purchases')
        ?.select('status, credits_purchased, stripe_receipt_url')
        ?.eq('user_id', userId)
        ?.eq('stripe_payment_id', intentId)
        ?.maybeSingle();

      if (!error && data?.status === 'completed') {
        return data;
      }

      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    return null;
  };

  const getPaymentErrorDetails = (error) => {
    if (!error) return null;

    const code = error?.code || '';
    const declineCode = error?.decline_code || '';
    const type = error?.type || '';

    if (type === 'api_connection_error' || code === 'api_connection_error') {
      return {
        title: 'Connection Error',
        message: 'Unable to reach the payment service. Please check your internet connection and try again.',
        icon: 'WifiOff',
        suggestion: 'Make sure you\'re connected to the internet, then retry.',
      };
    }

    if (declineCode === 'insufficient_funds') {
      return {
        title: 'Insufficient Funds',
        message: 'Your card was declined because it doesn\'t have enough funds to cover this purchase.',
        icon: 'Wallet',
        suggestion: 'Try a different card or add funds to your account.',
      };
    }

    if (declineCode === 'expired_card' || code === 'expired_card') {
      return {
        title: 'Card Expired',
        message: 'Your card has expired and cannot be used for this transaction.',
        icon: 'CalendarX',
        suggestion: 'Please use a card with a valid expiry date.',
      };
    }

    if (declineCode === 'incorrect_cvc' || code === 'incorrect_cvc') {
      return {
        title: 'Incorrect Security Code',
        message: 'The security code (CVV/CVC) you entered does not match the one on file for this card.',
        icon: 'ShieldAlert',
        suggestion: 'Double-check the 3- or 4-digit code on the back of your card.',
      };
    }

    if (declineCode === 'incorrect_number' || code === 'incorrect_number' || code === 'invalid_number') {
      return {
        title: 'Invalid Card Number',
        message: 'The card number you entered is not valid.',
        icon: 'CreditCard',
        suggestion: 'Please check your card number and try again.',
      };
    }

    if (declineCode === 'lost_card' || declineCode === 'stolen_card') {
      return {
        title: 'Card Declined',
        message: 'This card has been reported and cannot be used for payments.',
        icon: 'Ban',
        suggestion: 'Please contact your bank or use a different payment method.',
      };
    }

    if (declineCode === 'do_not_honor' || declineCode === 'transaction_not_allowed' || declineCode === 'card_velocity_exceeded') {
      return {
        title: 'Card Declined by Bank',
        message: 'Your bank declined this transaction. This can happen due to security restrictions or spending limits.',
        icon: 'ShieldX',
        suggestion: 'Contact your bank to authorise the payment, or try a different card.',
      };
    }

    if (type === 'card_error' || declineCode === 'generic_decline') {
      return {
        title: 'Card Declined',
        message: error?.message || 'Your card was declined. No charge has been made to your account.',
        icon: 'XCircle',
        suggestion: 'Please try a different card or contact your bank for more details.',
      };
    }

    if (type === 'validation_error') {
      return {
        title: 'Incomplete Payment Details',
        message: error?.message || 'Some payment details are missing or incorrect.',
        icon: 'AlertCircle',
        suggestion: 'Please review your card details and fill in all required fields.',
      };
    }

    return {
      title: 'Payment Failed',
      message: error?.message || 'An unexpected error occurred while processing your payment.',
      icon: 'AlertCircle',
      suggestion: 'Please try again. If the problem persists, contact support.',
    };
  };

  const extractExecutionId = (confirmError, confirmData) => {
    if (confirmData?.executionId) return confirmData?.executionId;
    const message = confirmError?.message || '';
    const match = message?.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    return match?.[0] || null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setPaymentError(null);
    setPaymentInfo(null);

    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/credit-purchase-success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        const details = getPaymentErrorDetails(error);
        setPaymentError(details);
        onError?.(error?.message);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        if (!user?.id) {
          setPaymentError({
            title: 'Authentication Required',
            message: 'Payment succeeded, but your session is missing. Please sign in again before we can deliver credits.',
            icon: 'ShieldAlert',
            suggestion: `Keep this transaction ID: TXN-${paymentIntent?.id?.slice(-12)?.toUpperCase()}`,
          });
          setIsProcessing(false);
          return;
        }

        const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
        const token = sessionData?.session?.access_token;

        if (sessionError || !token) {
          setPaymentError({
            title: 'Session Expired',
            message: 'Your payment was successful, but your session expired before credit delivery.',
            icon: 'AlertTriangle',
            suggestion: `Please sign in again and contact support with TXN-${paymentIntent?.id?.slice(-12)?.toUpperCase()}.`,
          });
          setIsProcessing(false);
          return;
        }

        const { data, error: confirmError } = await supabase?.functions?.invoke('confirm-payment', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            paymentIntentId: paymentIntent?.id,
            payment_intent_id: paymentIntent?.id,
            userId: user?.id,
          },
        });

        if (confirmError) {
          console.error('Confirm payment error:', confirmError);
        }

        if (data?.reconciliationPending) {
          const executionId = data?.executionId || extractExecutionId(confirmError, data);
          setPaymentInfo({
            title: 'Payment Verified, Credits Syncing',
            message: 'Your card payment is complete. We are finalizing credits in your account now.',
            hint: executionId ? `Ref: ${executionId}` : null,
          });
        }

        const completedPurchase = await waitForWebhookCompletion(paymentIntent?.id, user?.id, 15, 1000);

        if (!completedPurchase && confirmError) {
          const executionId = extractExecutionId(confirmError, data);
          const supportHint = executionId
            ? `Ref: ${executionId}`
            : `Transaction ID: TXN-${paymentIntent?.id?.slice(-12)?.toUpperCase()}`;

          setPaymentError({
            title: 'Credits Confirmation Delayed',
            message: 'Your payment was successful, but credit delivery is still pending confirmation.',
            icon: 'AlertTriangle',
            suggestion: `${supportHint} — please contact support if credits do not appear in a few minutes.`,
          });
          setIsProcessing(false);
          return;
        }

        if (user) {
          await refreshCredits();
        }

        const transactionId = `TXN-${paymentIntent?.id?.slice(-12)?.toUpperCase()}`;
        const date = new Date()?.toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        onSuccess({
          transactionId,
          paymentIntentId: paymentIntent?.id,
          packageInfo,
          creditsAdded: completedPurchase?.credits_purchased || data?.creditsAdded || data?.creditsToAdd || packageInfo?.credits,
          receiptUrl: completedPurchase?.stripe_receipt_url || null,
          subtotal,
          tax,
          total,
          email: user?.email || '',
          paymentMethod: 'Credit Card',
          date,
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      const isNetworkError =
        !navigator.onLine ||
        err?.message?.toLowerCase()?.includes('network') ||
        err?.message?.toLowerCase()?.includes('fetch');

      setPaymentError(
        isNetworkError
          ? {
              title: 'Network Error',
              message: 'Your payment could not be sent due to a network issue. No charge has been made.',
              icon: 'WifiOff',
              suggestion: 'Check your internet connection and try again.',
            }
          : {
              title: 'Unexpected Error',
              message: 'Something went wrong on our end. No charge has been made to your account.',
              icon: 'AlertTriangle',
              suggestion: 'Please wait a moment and try again. If the issue continues, contact support.',
            }
      );
      onError?.(err?.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>
      {paymentInfo && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={20} color="#2563EB" className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-blue-800 mb-0.5">{paymentInfo?.title}</p>
            <p className="text-sm text-blue-700 mb-1">{paymentInfo?.message}</p>
            {paymentInfo?.hint && (
              <p className="text-xs text-blue-600">{paymentInfo?.hint}</p>
            )}
          </div>
        </div>
      )}
      {paymentError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <Icon name={paymentError?.icon || 'AlertCircle'} size={20} color="#DC2626" className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-800 mb-0.5">{paymentError?.title}</p>
            <p className="text-sm text-red-700 mb-1">{paymentError?.message}</p>
            {paymentError?.suggestion && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <Icon name="Lightbulb" size={12} color="#DC2626" className="flex-shrink-0" />
                {paymentError?.suggestion}
              </p>
            )}
          </div>
        </div>
      )}
      <Button
        type="submit"
        variant="default"
        size="lg"
        className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700"
        disabled={!stripe || isProcessing || !user}
      >
        {isProcessing ? (
          <span className="flex items-center gap-2">
            <Icon name="Loader" size={20} className="animate-spin" />
            Processing Payment...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Icon name="Lock" size={20} />
            Pay ${total?.toFixed(2)}
          </span>
        )}
      </Button>
      <div className="mt-4 flex items-center justify-center gap-2 text-emerald-600">
        <Icon name="ShieldCheck" size={18} />
        <span className="text-sm font-medium">Secured by Stripe</span>
      </div>
    </form>
  );
};

// ─── Checkout ─────────────────────────────────────────────────────────────────

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, loading } = useAuth();

  const selectedPackage = useMemo(() => {
    if (location?.state?.package) return location?.state?.package;
    if (typeof window === 'undefined') return null;
    try {
      const stored = sessionStorage.getItem('checkoutSelectedPackage');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [location?.state?.package]);

  // hasInitialized guards the useEffect so it fires exactly once.
  // It must be set to true BEFORE any async operation that could trigger
  // a re-render, to prevent the useEffect from launching a concurrent
  // initializePayment() call while an applyCoupon/removeCoupon is in flight.
  const hasInitialized = useRef(false);

  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderData, setOrderData] = useState(null);

  // Coupon state — single source of truth for the applied coupon.
  // null = no coupon applied; object = coupon applied with discount details.
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Derived totals — always computed from appliedCoupon, never from stale closures.
  const price = selectedPackage?.price || 0;
  const discountAmount = appliedCoupon?.discountAmount || 0;
  const finalTotal = appliedCoupon ? appliedCoupon?.finalAmount : price;

  // ── Initial PaymentIntent creation ─────────────────────────────────────────
  useEffect(() => {
    if (!selectedPackage) {
      navigate('/purchase-credits');
      return;
    }

    // Wait for auth to finish loading before making any decisions.
    if (loading) return;

    // Only initialize once — prevent re-firing when user object refreshes.
    if (hasInitialized?.current) return;
    hasInitialized.current = true;

    if (!user) {
      setIsInitializing(false);
      setInitError('Please sign in again to continue with your purchase.');
      return;
    }

    // Call initializePayment with no coupon for the initial load.
    initializePayment({ coupon: null });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage, user, loading]);

  // ── Core PaymentIntent creator ─────────────────────────────────────────────
  /**
   * Creates a new Stripe PaymentIntent.
   *
   * Accepts an explicit `coupon` argument so it always uses the value
   * passed in at call time — never a stale closure value from state.
   *
   * @param {{ coupon: object|null }} options
   */
  const initializePayment = useCallback(async ({ coupon }) => {
    if (!selectedPackage) {
      setInitError('No package selected.');
      setIsInitializing(false);
      return;
    }

    if (!user) {
      setInitError('Please sign in before starting checkout.');
      setIsInitializing(false);
      return;
    }

    if (!stripePromise) {
      setInitError('Payment service is not configured. Please contact support.');
      setIsInitializing(false);
      return;
    }

    // Compute amounts from the explicit coupon argument — not from state.
    const couponDiscount = coupon?.discountAmount || 0;
    const chargeAmount = coupon ? coupon?.finalAmount : price;

    setIsInitializing(true);
    setInitError('');
    setClientSecret('');
    setPaymentIntentId('');

    try {
      const result = await createPaymentIntentWithAuthRetry(
        supabase,
        {
          packId: selectedPackage?.id,
          packName: selectedPackage?.name,
          amount: chargeAmount,
          currency: 'usd',
          credits: selectedPackage?.credits,
          ...(couponDiscount > 0 ? { discountAmount: couponDiscount } : {}),
          ...(coupon?.promotionCodeId ? { promotionCodeId: coupon?.promotionCodeId } : {}),
          customerInfo: {
            userId: user?.id,
            name: userProfile?.full_name || user?.email || '',
            email: user?.email || '',
            stripeCustomerId: null,
          },
        },
        {
          logger: (event, payload) => {
            console.log(`[checkout] ${event}`, payload ?? {});
          },
        }
      );

      if (!result?.ok) {
        if (result?.code === 'NOT_AUTHENTICATED' || result?.code === 'AUTH_RETRY_FAILED') {
          setInitError('Authentication failed. Please sign in again.');
          setTimeout(() => navigate('/auth', { state: { returnTo: '/checkout', package: selectedPackage } }), 2000);
        } else {
          setInitError(result?.message || 'Failed to initialize payment. Please try again.');
        }
        return;
      }

      const { clientSecret: secret, paymentIntentId: intentId } = result?.data ?? {};

      if (!secret || !intentId) {
        setInitError('Payment service returned incomplete data. Please try again.');
        return;
      }

      setClientSecret(secret);
      setPaymentIntentId(intentId);
    } catch (err) {
      const msg = err?.message?.toLowerCase?.() || '';
      const isNetworkError = !navigator.onLine || msg?.includes('network') || msg?.includes('fetch');
      setInitError(
        isNetworkError
          ? 'Network error — unable to connect to the payment service. Please check your internet connection and try again.' :'Failed to connect to the payment service. No charge has been made. Please try again.'
      );
    } finally {
      setIsInitializing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage, user, userProfile, price]);

  // ── Coupon handlers ────────────────────────────────────────────────────────

  /**
   * Called by CouponInput when a valid coupon is confirmed.
   * Sets the coupon state FIRST, then recreates the PaymentIntent
   * with the discounted amount — no race with useEffect because
   * hasInitialized.current is already true at this point.
   */
  const handleCouponApplied = useCallback(async (couponData) => {
    // hasInitialized is already true (set during initial load).
    // Setting it again here is a safety net in case handleRetry reset it.
    hasInitialized.current = true;

    setAppliedCoupon(couponData);

    // Recreate the PaymentIntent with the discounted amount.
    // Pass couponData explicitly so initializePayment doesn't read stale state.
    await initializePayment({ coupon: couponData });
  }, [initializePayment]);

  /**
   * Called by CouponInput when the coupon is removed.
   * Clears the coupon state FIRST, then recreates the PaymentIntent
   * at full price — again passing null explicitly to avoid stale state.
   */
  const handleCouponRemoved = useCallback(async () => {
    hasInitialized.current = true;

    setAppliedCoupon(null);

    // Recreate the PaymentIntent at full price.
    await initializePayment({ coupon: null });
  }, [initializePayment]);

  // ── Manual retry ───────────────────────────────────────────────────────────
  const handleRetry = () => {
    // Reset the init guard so the user can manually retry.
    hasInitialized.current = false;
    initializePayment({ coupon: appliedCoupon });
    // Re-set the guard after kicking off the async call.
    hasInitialized.current = true;
  };

  const handlePaymentSuccess = (purchaseData) => {
    if (typeof window !== 'undefined' && purchaseData?.packageInfo?.id) {
      if (user?.id) {
        sessionStorage.setItem(`recentlyPurchasedPackageId:${user?.id}`, purchaseData?.packageInfo?.id);
      }
      sessionStorage.removeItem('recentlyPurchasedPackageId');
      sessionStorage.setItem('lastSelectedPackageId', purchaseData?.packageInfo?.id);
      sessionStorage.removeItem('checkoutSelectedPackage');
    }
    setOrderData(purchaseData);
    navigate('/user-dashboard', { state: { paymentSuccess: true } });
  };

  const handlePaymentError = (errorMsg) => {
    console.error('Payment error:', errorMsg);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/user-dashboard');
  };

    if (!selectedPackage) return null;

  const stripeOptions = clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#9333EA',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#dc2626',
        fontFamily: 'system-ui, sans-serif',
        borderRadius: '8px',
      },
    },
  } : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pb-24">
      <Header
        userProp={user}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={async () => {
          await supabase?.auth?.signOut();
          navigate('/');
        }}
      />
      <main className="main-content px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={() => navigate('/purchase-credits')}
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-4"
            >
              <Icon name="ChevronLeft" size={20} />
              <span className="font-medium">Back to Packages</span>
            </button>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Complete Your Purchase
            </h1>
            <p className="text-muted-foreground">
              Secure checkout powered by Stripe
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order Summary - Sidebar */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="bg-card rounded-xl border border-border p-5 md:p-6 sticky top-4">
                <h2 className="text-xl font-bold text-foreground mb-4">Order Summary</h2>
                
                {/* Selected Package */}
                <div className="bg-gradient-to-r from-purple-50 to-cyan-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-14 h-14 md:w-12 md:h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                      <Icon name="Zap" size={28} color="#ffffff" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg md:text-base">{selectedPackage?.name}</h3>
                      <p className="text-base md:text-sm text-gray-600">{selectedPackage?.credits} Credits</p>
                    </div>
                  </div>
                  <ul className="space-y-2 md:space-y-1.5">
                    {selectedPackage?.features?.slice(0, 3)?.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="Check" size={18} color="#9333EA" className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm md:text-xs text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 mb-4">
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Subtotal</span>
                      <span className="text-sm font-medium text-foreground">${price?.toFixed(2)}</span>
                    </div>
                    {appliedCoupon && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-emerald-600 flex items-center gap-1">
                          <Icon name="Tag" size={14} color="#10b981" />
                          Discount ({appliedCoupon?.discountLabel})
                        </span>
                        <span className="text-sm font-medium text-emerald-600">-${appliedCoupon?.discountAmount?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-border pt-2 mt-2">
                      <span className="text-xl md:text-lg font-bold text-foreground">Total</span>
                      <span className="text-3xl md:text-2xl font-bold text-purple-600">${finalTotal?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Coupon Code Section — self-contained component */}
                <div className="mb-4">
                  <CouponInput
                    packageId={selectedPackage?.id}
                    appliedCoupon={appliedCoupon}
                    onCouponApplied={handleCouponApplied}
                    onCouponRemoved={handleCouponRemoved}
                    disabled={isInitializing}
                    supabase={supabase}
                  />
                </div>

                {/* Security Badge */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 md:p-3">
                  <div className="flex items-center gap-2">
                    <Icon name="Shield" size={20} color="#10b981" />
                    <span className="text-sm md:text-xs font-medium text-emerald-900">
                      Secure SSL Encrypted Payment
                    </span>
                  </div>
                </div>

                {/* Stripe Badge */}
                <div className="mt-3 flex items-center justify-center gap-2 text-gray-400">
                  <Icon name="Lock" size={14} />
                  <span className="text-xs">Powered by Stripe</span>
                </div>
              </div>
            </div>

            {/* Payment Form - Main Content */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <div className="bg-card rounded-xl border border-border p-5 md:p-6">
                <h3 className="text-xl md:text-lg font-bold text-foreground mb-6">Payment Details</h3>

                {isInitializing && (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                    <p className="text-muted-foreground">
                      {loading ? 'Verifying your session...' : 'Initializing secure payment...'}
                    </p>
                  </div>
                )}

                {initError && !isInitializing && (
                  <div className="py-8">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} color="#DC2626" className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-red-800 mb-1">Payment Initialization Failed</p>
                        <p className="text-sm text-red-700">{initError}</p>
                      </div>
                    </div>
                    {user ? (
                      <Button
                        variant="outline"
                        onClick={handleRetry}
                        iconName="RefreshCw"
                        iconPosition="left"
                        className="w-full"
                      >
                        Try Again
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        onClick={() => navigate('/sign-in')}
                        iconName="LogIn"
                        iconPosition="left"
                        className="w-full"
                      >
                        Sign In
                      </Button>
                    )}
                  </div>
                )}

                {!isInitializing && !initError && clientSecret && stripeOptions && (
                  <Elements stripe={stripePromise} options={stripeOptions}>
                    <StripePaymentForm
                      clientSecret={clientSecret}
                      paymentIntentId={paymentIntentId}
                      packageInfo={selectedPackage}
                      subtotal={price}
                      tax={0}
                      total={finalTotal}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  </Elements>
                )}

                {/* Terms */}
                <div className="mt-6 bg-muted/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={18} color="#6B7280" className="mt-0.5 flex-shrink-0" />
                    <p className="text-sm md:text-xs text-muted-foreground">
                      By completing this purchase, you agree to our Terms of Service and Privacy Policy.
                      Credits are non-refundable after 30 days.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Bottom Navigation */}
      <BottomNav activePath="/checkout" cameraInputId="camera-file-input" />
      {/* Hidden file input */}
      <input
        id="camera-file-input"
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/heic,image/jpg"
        multiple
      />
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Purchase Complete!"
        message="Your credits have been successfully added to your account. Start ranking your photos now!"
        orderDetails={orderData}
        ctaText="View Dashboard"
        ctaIcon="ArrowRight"
        onCtaClick={handleSuccessModalClose}
      />
    </div>
  );
};

export default Checkout;